import { jsx as _jsx } from "react/jsx-runtime";
import * as React from "react";
import { generatePath, RouterProvider } from "react-router-dom";
import "reflect-metadata";
import { createNamedRouter } from "./UI/ReactNamedRouter";
import { reaction } from "mobx";
import { NOT_LOADED } from "./Infra/RemoteData";
import { NamedRoutes } from "./UI/NamedRoutes";
import { updateHeapGlobalEventProperties } from "./Infra/LoadHeap";
import { computeProjectTypeLabel } from "healthex-common/heap.utils";
export const AppRouter = (props) => {
    var _a;
    const router = createNamedRouter(props.routerFactory, props.routingPaths(props.container));
    const initialMatch = router.state.matches.length ? router.state.matches[router.state.matches.length - 1] : null;
    const initialRoute = initialMatch && initialMatch.route;
    const initialNamedRoute = initialRoute ? router.getResolvedRoute(initialRoute) : null;
    const initialRouteParams = (_a = initialMatch === null || initialMatch === void 0 ? void 0 : initialMatch.params) !== null && _a !== void 0 ? _a : null;
    setDocumentTitle(initialRoute, initialRouteParams);
    updateHeapProjectId();
    updateHeapIASProperty();
    props.container.routingUIManager.initialize(initialNamedRoute, initialRouteParams, (currentRoute, currentRouteParams, isRedirect) => {
        const url = router.urlFromNamedRoute(currentRoute);
        if (url) {
            props.container.invalidationManager.invalidate();
            const urlWithParams = generatePath(url, currentRouteParams);
            router.navigate(urlWithParams, { replace: isRedirect });
            const { route, routeParams } = getCurrentRouteInfo();
            setDocumentTitle(route, routeParams);
            updateHeapProjectId();
            updateHeapIASProperty();
            window.scrollTo(0, 0);
        }
        else {
            console.log("WARNING: Attempted to navigate to an unbound route. Add this route to the configuration!");
        }
    });
    addEventListener("hashchange", (_event) => {
        const { route, namedRoute, routeParams } = getCurrentRouteInfo();
        setDocumentTitle(route, routeParams);
        updateHeapProjectId();
        updateHeapIASProperty();
        void props.container.routingUIManager.userNavigatedTo(namedRoute, routeParams !== null && routeParams !== void 0 ? routeParams : {});
    });
    reaction(() => props.container.studiesUIManager.studyToManage, () => updateHeapProjectId(), { fireImmediately: true });
    reaction(() => props.container.addProjectUIManager.study, () => updateHeapProjectId(), { fireImmediately: true });
    reaction(() => props.container.patientConsentUIManager.session.studyPublicInfo, () => updateHeapProjectId(), { fireImmediately: true });
    reaction(() => props.container.patientConsentUIManager.isIAS, () => updateHeapIASProperty(), { fireImmediately: true });
    return (_jsx(React.StrictMode, { children: _jsx(RouterProvider, { router: router }) }));
    function getCurrentRouteInfo() {
        var _a;
        const match = router.state.matches.length ? router.state.matches[router.state.matches.length - 1] : null;
        const route = match && match.route;
        const namedRoute = route ? router.getResolvedRoute(route) : null;
        const routeParams = (_a = match === null || match === void 0 ? void 0 : match.params) !== null && _a !== void 0 ? _a : null;
        return { route, namedRoute, routeParams };
    }
    function setDocumentTitle(route, routeParams) {
        const defaultTitle = "HealthEx";
        if (!(route === null || route === void 0 ? void 0 : route.title)) {
            document.title = defaultTitle;
        }
        else {
            if (typeof route.title === "function") {
                if (routeParams) {
                    document.title = `${defaultTitle} | ${route.title(routeParams)}`;
                }
                else {
                    document.title = defaultTitle;
                }
            }
            else {
                document.title = `${defaultTitle} | ${route.title}`;
            }
        }
    }
    function updateHeapProjectId() {
        var _a, _b, _c, _d, _e, _f;
        try {
            const matchWithId = router.state.matches
                .slice()
                .reverse()
                .find((m) => { var _a, _b, _c; return (_b = (_a = m === null || m === void 0 ? void 0 : m.params) === null || _a === void 0 ? void 0 : _a.projectId) !== null && _b !== void 0 ? _b : (_c = m === null || m === void 0 ? void 0 : m.params) === null || _c === void 0 ? void 0 : _c.studyId; });
            const projectId = (_b = (_a = matchWithId === null || matchWithId === void 0 ? void 0 : matchWithId.params) === null || _a === void 0 ? void 0 : _a.projectId) !== null && _b !== void 0 ? _b : (_c = matchWithId === null || matchWithId === void 0 ? void 0 : matchWithId.params) === null || _c === void 0 ? void 0 : _c.studyId;
            if (!projectId) {
                heap.removeEventProperty("projectId");
                heap.removeEventProperty("projectType");
                updateHeapGlobalEventProperties({ projectId: null, projectType: null });
                return;
            }
            heap.addEventProperties({ projectId });
            try {
                const fromStudyManager = props.container.studiesUIManager.studyToManage;
                const fromAddProjectManager = props.container.addProjectUIManager.study;
                const fromPatientConsentManager = props.container.patientConsentUIManager.session.studyPublicInfo;
                let projectTypeLabel = (_e = (_d = extractProjectTypeLabel(fromStudyManager, projectId)) !== null && _d !== void 0 ? _d : extractProjectTypeLabel(fromAddProjectManager, projectId)) !== null && _e !== void 0 ? _e : extractProjectTypeLabel(fromPatientConsentManager, projectId);
                if (!projectTypeLabel) {
                    const isAddProjectRoute = ((_f = props.container.routingUIManager.currentRoute) === null || _f === void 0 ? void 0 : _f.routeName) === NamedRoutes.PROJECTS_ADD_PROJECT.routeName;
                    if (isAddProjectRoute) {
                        const formProjectType = props.container.addProjectUIManager.studyFormModel.input.projectType;
                        const isPddeProvisional = props.container.authManager.userType === "EXTERNAL_RESEARCHER";
                        projectTypeLabel = computeProjectTypeLabel(formProjectType, isPddeProvisional);
                    }
                }
                if (projectTypeLabel) {
                    heap.addEventProperties({ projectType: projectTypeLabel });
                }
                else {
                    heap.removeEventProperty("projectType");
                }
                updateHeapGlobalEventProperties({ projectId, projectType: projectTypeLabel !== null && projectTypeLabel !== void 0 ? projectTypeLabel : null });
            }
            catch (error) {
                console.error("Error setting Heap event properties", error);
            }
        }
        catch (error) {
            console.error("Error setting Heap projectId", error);
        }
    }
    function updateHeapIASProperty() {
        try {
            const isIAS = props.container.patientConsentUIManager.isIAS;
            if (isIAS) {
                heap.addEventProperties({ isIAS: true });
            }
            else {
                heap.removeEventProperty("isIAS");
            }
            updateHeapGlobalEventProperties({ isIAS: isIAS ? true : null });
        }
        catch (error) {
            console.error("Error setting Heap isIAS property", error);
        }
    }
    function extractProjectTypeLabel(loadableStudy, expectedId) {
        if (loadableStudy === NOT_LOADED || loadableStudy === null)
            return null;
        if (loadableStudy.id !== expectedId)
            return null;
        return computeProjectTypeLabel(loadableStudy.projectType, loadableStudy.pdda);
    }
};
